import { CommunicationApi } from '@app/api';
import { Communication, CommunicationPreview } from '@app/model/Communication';
import { toApiError } from '@app/utils/ApiError';
import {
  Alert,
  Bullseye,
  Button,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Form,
  FormGroup,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Spinner,
  TextInput,
  Title
} from '@patternfly/react-core';
import * as React from 'react';

export interface CommunicationPreviewModalProps {
  /** The communication to render. Null closes the modal. */
  communication: Communication | null;
  onClose(): void;
}

/**
 * What a communication says, rendered without sending it.
 *
 * **It renders what is stored, not what is in the editor.** The endpoint resolves the communication
 * the same way a send would, so this answers "what does this say now" rather than "what would my
 * change produce" — which is why it is reached from the row rather than from inside the form, and
 * why the modal says so.
 *
 * The one place in the app that reports a render failure instead of falling back. On the send path
 * imperfect wording beats no notification, so the service quietly uses the catalog default; here
 * somebody is waiting to be told, so `problems` is shown.
 */
export const CommunicationPreviewModal: React.FunctionComponent<CommunicationPreviewModalProps> = ({
  communication,
  onClose
}) => {
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [preview, setPreview] = React.useState<CommunicationPreview | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [failure, setFailure] = React.useState<string | null>(null);

  const key = communication?.name;

  const render = React.useCallback(async (sample: Record<string, string>) => {
    if (!key) return;

    setIsLoading(true);
    setFailure(null);
    try {
      setPreview(await CommunicationApi.preview(key, sample));
    } catch (err) {
      setPreview(null);
      setFailure(toApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [key]);

  // Rendered on open with nothing filled in, which is useful rather than empty: an unsupplied
  // placeholder comes back as its own name in braces, so the output shows where a value would go.
  React.useEffect(() => {
    setValues({});
    setPreview(null);
    if (key) void render({});
  }, [key, render]);

  if (!communication) return null;

  return (
    <Modal
      variant={ModalVariant.large}
      isOpen
      onClose={onClose}
      aria-label={`Preview ${communication.name}`}
    >
      <ModalHeader
        title={`Preview — ${communication.name}`}
        description="Rendered from what is saved, so an edit shows up here once it has been saved."
      />
      <ModalBody>
        {communication.placeholders.length > 0 && (
          <>
            <Title headingLevel="h3" size="md">Sample values</Title>
            <Content component="p" style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>
              What a producer would pass. Leave one blank to see where it goes.
            </Content>
            <Form onSubmit={event => { event.preventDefault(); void render(values); }}>
              <Grid hasGutter>
                {communication.placeholders.map(placeholder => (
                  <GridItem span={6} key={placeholder}>
                    <FormGroup label={placeholder} fieldId={`preview-${placeholder}`}>
                      <TextInput
                        id={`preview-${placeholder}`}
                        aria-label={placeholder}
                        value={values[placeholder] ?? ''}
                        onChange={(_event, next) =>
                          setValues(current => ({ ...current, [placeholder]: next }))}
                      />
                    </FormGroup>
                  </GridItem>
                ))}
              </Grid>
            </Form>
            <Button
              variant="secondary"
              isDisabled={isLoading}
              onClick={() => void render(values)}
              style={{ marginTop: 'var(--pf-t--global--spacer--md)' }}
            >
              Render
            </Button>
          </>
        )}

        {isLoading && (
          <Bullseye style={{ padding: 'var(--pf-t--global--spacer--xl)' }}>
            <Spinner aria-label="Rendering the preview" />
          </Bullseye>
        )}

        {failure && (
          <Alert
            variant="danger"
            isInline
            title={failure}
            style={{ marginTop: 'var(--pf-t--global--spacer--md)' }}
          />
        )}

        {!isLoading && preview && (
          <div style={{ marginTop: 'var(--pf-t--global--spacer--lg)' }}>
            {preview.problems.length > 0 && (
              <Alert
                variant="warning"
                isInline
                title="Part of this communication did not render"
                style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
              >
                {preview.problems.map(problem => (
                  <Content component="p" key={problem}>{problem}</Content>
                ))}
              </Alert>
            )}

            <DescriptionList isHorizontal termWidth="10ch">
              <DescriptionListGroup>
                <DescriptionListTerm>Subject</DescriptionListTerm>
                <DescriptionListDescription>
                  <Rendered text={preview.subject} />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Message</DescriptionListTerm>
                <DescriptionListDescription>
                  <Rendered text={preview.message} />
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>

            {/* Out of the description list rather than a third row of it: the frame is far taller
                than a line of text, and PatternFly centres a horizontal term against its
                description, which left the label floating beside the middle of the mail. */}
            <Title headingLevel="h3" size="md" style={{ marginTop: 'var(--pf-t--global--spacer--lg)' }}>
              Email body
            </Title>
            <div style={{ marginTop: 'var(--pf-t--global--spacer--sm)' }}>
              {preview.emailBody ? <EmailBody html={preview.emailBody} /> : <Rendered text={null} />}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="link" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

const Rendered: React.FunctionComponent<{ text: string | null }> = ({ text }) =>
  text ? (
    <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>
  ) : (
    <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
      nothing — this communication has no template for it
    </span>
  );

/**
 * The mail as it will look, in a sandboxed frame.
 *
 * A frame rather than dangerouslySetInnerHTML, and the sandbox is the point rather than the
 * layout: this markup is written by an administrator, stored in a column, and shown to whoever
 * opens this screen next. An empty `sandbox` allows nothing at all — no scripts, no forms, no
 * same-origin access — so a template can only ever draw itself. It also keeps the mail's own CSS
 * off the surrounding page, which `dangerouslySetInnerHTML` would not.
 */
const EmailBody: React.FunctionComponent<{ html: string }> = ({ html }) => (
  <iframe
    title="Rendered email body"
    sandbox=""
    srcDoc={html}
    style={{
      width: '100%',
      minHeight: '12rem',
      border: '1px solid var(--pf-t--global--border--color--default)',
      borderRadius: 'var(--pf-t--global--border--radius--small)',
      background: 'var(--pf-t--global--background--color--primary--default)'
    }}
  />
);
